import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { cx } from '@platform/core/lib/cx';
import type { IDialogProps } from './IDialogProps';
import { focusableIn, trapTab } from './focusTrap';

/**
 * Модальне вікно (UIUX/layout/dialogs): затемнення rgba(ink, .4), радіус 14px, шапка «заголовок + підзаголовок + дії + ×» (кругла кнопка
 * з символом «×», як у прототипі), тіло з власною прокруткою, футер дій. Закривається Esc, кліком по тлу і ×; утримує фокус усередині
 * й повертає його після закриття.
 */
export function Dialog({ title, subtitle, actions, onClose, children, footer, size = 'sm' }: IDialogProps) {
  const { t } = useTranslation();
  const box = useRef<HTMLDivElement>(null);
  const close = useRef(onClose);
  useEffect(() => { close.current = onClose; }, [onClose]);
  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    const root = box.current;
    if (root) (focusableIn(root).find((el) => !el.classList.contains('dialog__close')) ?? root).focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.stopPropagation(); close.current(); } else if (root) trapTab(root, e);
    };
    window.addEventListener('keydown', onKey);
    return () => { window.removeEventListener('keydown', onKey); previous?.focus?.(); };
  }, []);
  return (
    <div className="dialog__backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div ref={box} className={cx('dialog', `dialog--${size}`)} role="dialog" aria-modal="true" aria-label={title} tabIndex={-1}>
        <div className="dialog__head">
          <div className="dialog__titles">
            <h2 className="dialog__title">{title}</h2>
            {subtitle && <div className="dialog__subtitle">{subtitle}</div>}
          </div>
          {actions}
          <button type="button" className="icon-btn icon-btn--md icon-btn--ghost icon-btn--round dialog__close" aria-label={t('app.close')} title={t('app.close')}
            onClick={onClose}>×</button>
        </div>
        <div className="dialog__body">{children}</div>
        {footer && <div className="dialog__foot">{footer}</div>}
      </div>
    </div>
  );
}
